import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconAdjustmentsHorizontal, IconPencil, IconPlus } from '@tabler/icons-react';
import { useStyles } from '../styles';
import {
  CompanyDto,
  apiService,
  useCompanyListControllerCompanyListQuery,
} from '@shared/services/apiService/apiService';
import { TableActionMenu, TableEmptyRow } from '@shared/components/listPage';
import { CreateCompanyPopup } from '@widgets/Popup/CreateCompany/CreateCompany';
import { SuperAdminAuthenticatedPath } from '@shared/constants/routes.constants';
import { EditCompanyPopup } from '@widgets/Popup/EditCompanyPopup/EditCompanyPopup';
import { CompanyLimitsPopup } from '@widgets/Popup/CompanyLimitsPopup/CompanyLimitsPopup';
import { ConcurrentCallsCell } from './ConcurrentCallsCell';
import { UsageLimitRatioCell } from './UsageLimitRatioCell';
import { COMPANY_USAGE_METRIC_COLUMNS } from '../usageLimitTableConfig';
import { useAppDispatch } from '@shared/store/store';

type CompanyDialog = 'details' | 'limits';

export const CompaniesPage = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigator = useNavigate();
  const dispatch = useAppDispatch();
  const [showCreateCompanyDialogFlag, setShowCreateCompanyDialogFlag] = useState(false);
  const [dialog, setDialog] = useState<CompanyDialog | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyDto | null>(null);

  const { data, isLoading, refetch: refetchCompanies } = useCompanyListControllerCompanyListQuery();
  // Name, usage, created date and actions; description and address live in "Edit details".
  const columnCount = 4 + COMPANY_USAGE_METRIC_COLUMNS.length;

  const closeCreateCompanyDialog = () => {
    setShowCreateCompanyDialogFlag(false);
    refetchCompanies();
  };

  const openDialog = (company: CompanyDto, kind: CompanyDialog) => {
    setSelectedCompany(company);
    setDialog(kind);
  };

  const handleSaved = () => {
    refetchCompanies();
    if (selectedCompany) {
      dispatch(
        apiService.endpoints.usageLimitControllerAdminUsageLimits.initiate(
          { id: selectedCompany.id },
          { forceRefetch: true },
        ),
      );
    }
  };

  const handleRowClick = (company: CompanyDto) => {
    navigator(`${SuperAdminAuthenticatedPath.EDIT_COMPANY}?id=${company.id}&name=${company.name}`);
  };

  return (
    <Box className={classes.main}>
      <Box className={classes.header}>
        <Typography variant="h4" fontWeight={600}>
          {t('superAdminMainPage.menuItems.companies')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<IconPlus size={18} />}
          onClick={() => setShowCreateCompanyDialogFlag(true)}>
          {t('superAdminMainPage.companies.buttons.createCompany')}
        </Button>
      </Box>
      <TableContainer component={Paper} className={classes.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('superAdminMainPage.companies.tableHeaders.name')}</TableCell>
              <TableCell className={classes.noWrap}>
                {t('superAdminMainPage.companies.tableHeaders.concurrentCalls')}
              </TableCell>
              {COMPANY_USAGE_METRIC_COLUMNS.map((col) => (
                <TableCell key={col.headerKey} className={classes.noWrap}>
                  {t(`superAdminMainPage.companies.tableHeaders.${col.headerKey}`)}
                </TableCell>
              ))}
              <TableCell>{t('superAdminMainPage.companies.tableHeaders.createdAt')}</TableCell>
              <TableCell align="right">
                {t('superAdminMainPage.companies.tableHeaders.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {data?.map((company) => (
              <TableRow
                onClick={() => handleRowClick(company)}
                key={company.id}
                className={classes.row}
                hover>
                <TableCell>
                  <Typography fontWeight={600}>{company.name}</Typography>
                  {company.industryType && (
                    <Typography variant="caption" color="text.secondary">
                      {company.industryType}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <ConcurrentCallsCell companyId={company.id} />
                </TableCell>
                {COMPANY_USAGE_METRIC_COLUMNS.map((col) => (
                  <TableCell key={col.headerKey}>
                    <UsageLimitRatioCell companyId={company.id} unit={col.unit} mode={col.mode} />
                  </TableCell>
                ))}
                <TableCell className={classes.noWrap}>
                  {new Date(company.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <TableActionMenu
                    ariaLabel={t('superAdminMainPage.companies.tableHeaders.actions')}
                    items={[
                      {
                        label: t('superAdminMainPage.companies.menu.editCompanyDetails'),
                        icon: <IconPencil size={16} />,
                        onClick: () => openDialog(company, 'details'),
                      },
                      {
                        label: t('superAdminMainPage.companies.menu.editLimits'),
                        icon: <IconAdjustmentsHorizontal size={16} />,
                        onClick: () => openDialog(company, 'limits'),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && !data?.length && (
              <TableEmptyRow
                colSpan={columnCount}
                message={t('superAdminMainPage.companies.empty')}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CreateCompanyPopup
        showDialogFlag={showCreateCompanyDialogFlag}
        closeDialog={closeCreateCompanyDialog}
      />
      {selectedCompany && (
        <EditCompanyPopup
          company={selectedCompany}
          showDialogFlag={dialog === 'details'}
          closeDialog={() => setDialog(null)}
          onCompanyEditSuccess={handleSaved}
        />
      )}
      {selectedCompany && (
        <CompanyLimitsPopup
          companyId={selectedCompany.id}
          showDialogFlag={dialog === 'limits'}
          closeDialog={() => setDialog(null)}
          onSuccess={handleSaved}
        />
      )}
    </Box>
  );
};
